// Mentor "Brand" — an optional coaching brand/institute shown as a pill on the mentor card
// and profile, opening a popup with the brand's website + social links. Pure/testable.
// Security: every URL is sanitized to http/https only (blocks javascript:/data: → XSS).

export interface Brand {
  name: string;
  tagline: string | null;
  logoUrl: string | null;
  website: string | null;
  x: string | null;
  facebook: string | null;
  linkedin: string | null;
  instagram: string | null;
  youtube: string | null;
}

const MAX_URL = 300;

// Returns a safe absolute http(s) URL, or null. Prepends https:// to a bare domain.
export function sanitizeUrl(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim().slice(0, MAX_URL);
  if (!s) return null;
  const withScheme = /^https?:\/\//i.test(s) ? s : (/^[\w-]+(\.[\w-]+)+/.test(s) ? `https://${s}` : '');
  if (!withScheme) return null;
  try {
    const u = new URL(withScheme);
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u.toString() : null;
  } catch { return null; }
}

// Accept a full URL, a bare domain, or an @handle; build the platform URL for a handle.
function handleUrl(raw: string | null | undefined, base: string): string | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s) || /^[\w-]+(\.[\w-]+)+/.test(s)) return sanitizeUrl(s);
  const handle = s.replace(/^@/, '').replace(/[^\w.-]/g, '').slice(0, 80);
  return handle ? sanitizeUrl(base + handle) : null;
}

export interface BrandInput {
  name?: string; tagline?: string; logoUrl?: string | null;
  website?: string; x?: string; facebook?: string; linkedin?: string; instagram?: string; youtube?: string;
}

// Normalize raw form input into a safe, storable Brand — or null when no name (brand cleared).
export function normalizeBrand(input: BrandInput): Brand | null {
  const name = (input.name ?? '').trim().slice(0, 80);
  if (!name) return null;
  return {
    name,
    tagline: (input.tagline ?? '').trim().slice(0, 140) || null,
    logoUrl: sanitizeUrl(input.logoUrl ?? null),
    website: sanitizeUrl(input.website),
    x: handleUrl(input.x, 'https://x.com/'),
    facebook: handleUrl(input.facebook, 'https://facebook.com/'),
    linkedin: handleUrl(input.linkedin, 'https://linkedin.com/in/'),
    instagram: handleUrl(input.instagram, 'https://instagram.com/'),
    youtube: handleUrl(input.youtube, 'https://youtube.com/@'),
  };
}

// Parse an unknown value (Firestore / API payload) into a safe Brand, or null.
export function readBrand(v: unknown): Brand | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const str = (k: string) => (typeof o[k] === 'string' ? (o[k] as string) : null);
  const name = (str('name') ?? '').trim();
  if (!name) return null;
  return {
    name: name.slice(0, 80),
    tagline: (str('tagline') ?? '').trim().slice(0, 140) || null,
    logoUrl: sanitizeUrl(str('logoUrl')),
    website: sanitizeUrl(str('website')),
    x: sanitizeUrl(str('x')),
    facebook: sanitizeUrl(str('facebook')),
    linkedin: sanitizeUrl(str('linkedin')),
    instagram: sanitizeUrl(str('instagram')),
    youtube: sanitizeUrl(str('youtube')),
  };
}

export function hasBrand(b: Brand | null | undefined): b is Brand {
  return !!b && !!b.name;
}

export interface BrandLink { key: string; label: string; href: string; icon: string }

// The provided links, in display order (only ones the mentor filled in).
export function brandLinks(b: Brand): BrandLink[] {
  const out: BrandLink[] = [];
  if (b.website)   out.push({ key: 'website',   label: 'Website',   href: b.website,   icon: '🌐' });
  if (b.instagram) out.push({ key: 'instagram', label: 'Instagram', href: b.instagram, icon: '📸' });
  if (b.youtube)   out.push({ key: 'youtube',   label: 'YouTube',   href: b.youtube,   icon: '▶️' });
  if (b.x)         out.push({ key: 'x',         label: 'X',         href: b.x,         icon: '✖️' });
  if (b.facebook)  out.push({ key: 'facebook',  label: 'Facebook',  href: b.facebook,  icon: '📘' });
  if (b.linkedin)  out.push({ key: 'linkedin',  label: 'LinkedIn',  href: b.linkedin,  icon: '💼' });
  return out;
}
